import { styled } from '@mui/material';
import AssignmentSettings from 'components/ViewAssignment/AssignmentSettings';
import InstantStudentFeedback from 'components/ViewAssignment/InstantStudentFeedback';
import PreviewAndGrading from 'components/ViewAssignment/PreviewAndGrading';
import { useAuth } from 'contexts/auth';
import { useTranslation } from 'react-i18next';
import { Button, Container, Divider, Icon, Paper, RubricTable, Stack, Text, Tooltip, ViewMore } from 'shell/components';
import { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';
import { getTooltipComponent } from 'utils/helper';

const Tablet = ({
    assignment,
    isDuplicating,
    isPreviewGenerating,
    isSubmissionsGenerating,
    onClickBack,
    onClickDuplicate,
    onClickUnpublish,
    onClickPublish,
    onClickEdit,
    onClickViewBaseline,
    onClickViewContentBank,
    onClickViewGradingSettings,
}) => {
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');
    const { AccordionRubricTableButton, isExpanded } = useViewAccordionRubricTableButton();

    return (
        <Container device={'tablet'}>
            <CustomStack spacing={5}>
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Stack spacing={2}>
                    <Text sx={{ fontStyle: 'italic' }} color={'light'}>
                        {assignment.is_draft
                            ? t('common:status.draft', 'Draft')
                            : t('common:status.published', 'Published')}
                    </Text>
                    <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {assignment.title}
                        </Text>
                        <Stack direction={'row'} spacing={4}>
                            <Button
                                loading={isDuplicating}
                                variant={'tertiary'}
                                onClick={() => onClickDuplicate(assignment)}
                            >
                                {t('common:controls.duplicate', 'Duplicate')}
                            </Button>
                            <Button
                                disabled={assignment.api_assignment_id && auth.role === 'ta'}
                                variant={'tertiary'}
                                onClick={
                                    assignment.is_draft
                                        ? () => onClickPublish(assignment)
                                        : () => onClickUnpublish(assignment)
                                }
                            >
                                {assignment.is_draft
                                    ? t('common:controls.publish', 'Publish')
                                    : t('common:controls.unpublish', 'Unpublish')}
                            </Button>
                            <Button
                                disabled={
                                    isPreviewGenerating ||
                                    isSubmissionsGenerating ||
                                    (assignment.api_assignment_id && auth.role === 'ta')
                                }
                                startIcon={<Icon kind={'edit'} />}
                                tooltip={
                                    isPreviewGenerating || isSubmissionsGenerating
                                        ? {
                                              component: t(
                                                  'general-section.edit-disabled-tooltip',
                                                  'Please wait until submissions are uploaded before continuing to edit.'
                                              ),
                                          }
                                        : undefined
                                }
                                variant={'tertiary'}
                                onClick={onClickEdit}
                            >
                                {t('common:controls.edit', 'Edit')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack spacing={2}>
                    <Paper variant={'outlined'}>
                        <PreviewAndGrading />
                    </Paper>
                    {process.env.REACT_APP_AUTH !== 'PASSWORD' && (
                        <Paper variant={'outlined'}>
                            <InstantStudentFeedback assignment={assignment} />
                        </Paper>
                    )}
                </Stack>
                <CustomPaper elevation={0}>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Text>{t('general-section.assignment-description-label', 'Assignment Description')}</Text>
                            {assignment.description ? (
                                <ViewMore color={'light'} rich text={assignment.description} />
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                                {assignment.rubric && <AccordionRubricTableButton />}
                            </Stack>
                            {assignment.rubric ? (
                                <RubricTable type={'view'} isExpanded={isExpanded} data={assignment.rubric.criteria} />
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        {assignment.baseline && (
                            <Stack divider={<Divider />} spacing={2}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>{t('general-section.baseline', 'Baseline')}</Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t('assignment.assignment-baseline', 'Assignment Baseline'),
                                            t(
                                                'assignment.creating-a-baseline-description',
                                                'For optimal AI feedback and grading outcomes, we recommend uploading and grading a previous assignment. This will help align the AI with your assessment practices.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                                <Button
                                    sx={{ alignSelf: 'flex-start' }}
                                    variant={'borderless'}
                                    onClick={onClickViewBaseline}
                                >
                                    {t('general-section.view-assignment-baseline', 'View Assignment Baseline')}
                                </Button>
                            </Stack>
                        )}
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('citation-format.label', 'Citation Format')}</Text>
                                <Tooltip
                                    content={t(
                                        'citation-format.tooltip',
                                        'Grading adapts to your chosen citation style and applies the correct formatting standards (e.g., APA first-page formatting is evaluated differently).'
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            <Text color={'light'}>
                                {t(`citation-format.option.${assignment.citation_format ?? 'none'}`)}
                            </Text>
                        </Stack>
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('content-bank.title', 'Content Bank')}</Text>
                                <Tooltip
                                    content={t(
                                        'content-bank.secondary-tooltip',
                                        'Files added to the Content Bank allow our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    )}
                                    type={'info'}
                                />
                            </Stack>
                            {assignment.content_bank_resources?.filter(({ active }) => active)?.length ? (
                                <Button
                                    sx={{ alignSelf: 'flex-start' }}
                                    variant={'borderless'}
                                    onClick={onClickViewContentBank}
                                >
                                    {t('content-bank.view-content-bank', 'View Content Bank')}
                                </Button>
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <AssignmentSettings
                            assignment={assignment}
                            onClickViewGradingSettings={onClickViewGradingSettings}
                        />
                    </Stack>
                </CustomPaper>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(5),
    };
});

export default Tablet;
